import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import API from "../services/api";
import SearchableSelect from "./SearchableSelect";
import CreateJobFormNew from "./NewCreateForm";
import "../styles/job-drawer.css";

const getApiUrl = () => {
  if (API?.defaults?.baseURL) {
    return API.defaults.baseURL;
  }
  const url = process.env.REACT_APP_API_URL || (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL);
  if (url && typeof url === "string" && url.trim() !== "") {
    return url.trim();
  }
  return "https://jobtracking.thesalonx.in/api";
};

const getApiOrigin = () => {
  return getApiUrl().replace(/\/api\/?$/, "");
};

const downloadImage = async (url, filename = "photo.jpg") => {
  if (!url) return;
  let finalFileName = filename || "photo.jpg";
  if (!finalFileName.includes(".")) finalFileName += ".jpg";

  // Handle data: or blob: directly
  if (typeof url === "string" && (url.startsWith("data:") || url.startsWith("blob:"))) {
    const link = document.createElement("a");
    link.href = url;
    link.download = finalFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  let rawStr = url;
  if (typeof url === "object" && url !== null) {
    rawStr = url.url || url.filename || url.photo || "";
  }
  let basename = String(rawStr).split("/").pop() || "photo.jpg";
  if (basename.includes("?")) basename = basename.split("?")[0];

  // 1. Try backend download endpoint via Axios API instance (bypasses CORS & handles blob response)
  try {
    const res = await API.get(`/download-photo?path=${encodeURIComponent(basename)}`, {
      responseType: "blob",
    });
    if (res.data && res.data.size > 0) {
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = finalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      return;
    }
  } catch (err) {
    console.warn("API photo download fallback:", err);
  }

  // 2. Direct fetch candidates fallback
  const candidates = getCandidatePhotoUrls(url);
  for (const src of candidates) {
    try {
      const response = await fetch(src);
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = finalFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
        return;
      }
    } catch (e) {}
  }

  // 3. Last fallback: Open image in new window/tab
  const fallbackUrl = candidates[0] || (typeof url === "string" ? url : "");
  if (fallbackUrl) {
    window.open(fallbackUrl, "_blank");
  }
};

const getCandidatePhotoUrls = (filenameOrUrl) => {
  if (!filenameOrUrl) return [];
  let str = filenameOrUrl;
  if (typeof filenameOrUrl === "object" && filenameOrUrl !== null) {
    str = filenameOrUrl.url || filenameOrUrl.photo || filenameOrUrl.filename || filenameOrUrl.path || "";
  }
  if (typeof str !== "string") return [];
  str = str.trim();
  if (!str) return [];

  if (str.startsWith("data:") || str.startsWith("blob:")) return [str];

  let basename = str.split("/").pop() || "";
  if (basename.includes("?")) basename = basename.split("?")[0];

  const origin = getApiOrigin();
  const candidates = [];

  if (str.startsWith("http://") || str.startsWith("https://")) {
    candidates.push(str);
    try {
      const parsedUrl = new URL(str);
      if (parsedUrl.pathname) {
        candidates.push(`${origin}${parsedUrl.pathname}`);
      }
    } catch (e) {}
  } else {
    const cleanPath = str.replace(/^\/+/, "");
    candidates.push(`${origin}/${cleanPath}`);
  }

  if (basename) {
    candidates.push(`${origin}/job_photos/articles/${basename}`);
    candidates.push(`${origin}/uploads/job/${basename}`);
    candidates.push(`${origin}/job_photos/rows/${basename}`);
    candidates.push(`${origin}/job_photos/before/${basename}`);
    candidates.push(`${origin}/job_photos/after/${basename}`);
    candidates.push(`${origin}/job_photos/job_cards/${basename}`);
    candidates.push(`${origin}/job_photos/${basename}`);
  }

  return Array.from(new Set(candidates));
};

const toPhotoUrl = (filename) => {
  if (!filename) return null;
  let nameStr = filename;
  if (typeof filename === "object" && filename !== null) {
    nameStr = filename.url || filename.filename || filename.photo;
  }
  if (!nameStr || typeof nameStr !== "string") return null;
  const trimmed = nameStr.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("data:") || trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("blob:")) {
    return trimmed;
  }

  const cleanPath = trimmed.replace(/^\/+/, "");
  if (cleanPath.includes("/")) {
    return `${getApiOrigin()}/${cleanPath}`;
  }

  const basename = trimmed.split("/").pop();
  return `${getApiOrigin()}/job_photos/articles/${basename}`;
};

function toPhotoList(list) {
  if (!list) return [];
  const arr = Array.isArray(list) ? list : [list];
  return arr.map((p) => {
    if (!p) return null;
    if (typeof p === "string") {
      return { id: null, url: toPhotoUrl(p), filename: p };
    }
    const filename = p.photo || p.filename || p.url;
    return { id: p.id ?? null, url: toPhotoUrl(filename), filename };
  }).filter(Boolean);
}

function cleanStaffName(rawName, globalStaffList = []) {
  if (!rawName) return null;
  const str = String(rawName).trim();
  if (str.startsWith("Staff #")) {
    const extracted = str.replace("Staff #", "").replace(/[[\]]/g, "").trim();
    const ids = extracted.split(",").map((x) => x.trim()).filter(Boolean);
    const staffMap = {};
    (globalStaffList || []).forEach((s) => {
      if (s && s.id) staffMap[String(s.id)] = s.name;
    });
    const names = ids.map((id) => staffMap[id]).filter(Boolean);
    if (names.length > 0) return names.join(", ");
  }
  return str;
}

function checkIsTeamLeader(user, job) {
  if (!user) return false;

  const role = (user?.role || "").toLowerCase().replace(/_/g, " ");
  if (["admin", "team leader", "team_leader", "tl", "teamleader"].includes(role)) {
    return true;
  }
  if (!job) return false;

  const currentUserId = user.id != null ? String(user.id).trim() : (user.user_id != null ? String(user.user_id).trim() : null);
  const currentUserName = user.name ? String(user.name).trim().toLowerCase() : "";

  const extractTLInfos = (tlProp, tlIdProp) => {
    const ids = new Set();
    const names = new Set();

    if (tlIdProp != null) {
      if (Array.isArray(tlIdProp)) {
        tlIdProp.forEach((id) => id != null && ids.add(String(id).trim()));
      } else if (typeof tlIdProp === "string" && tlIdProp.startsWith("[")) {
        try {
          JSON.parse(tlIdProp).forEach((id) => id != null && ids.add(String(id).trim()));
        } catch (e) {
          tlIdProp.replace(/[[\]]/g, "").split(",").forEach((id) => ids.add(id.trim()));
        }
      } else {
        ids.add(String(tlIdProp).trim());
      }
    }

    if (tlProp != null) {
      if (Array.isArray(tlProp)) {
        tlProp.forEach((item) => {
          if (typeof item === "object" && item !== null) {
            if (item.id != null) ids.add(String(item.id).trim());
            if (item.name) names.add(String(item.name).trim().toLowerCase());
          } else if (typeof item === "number" || (typeof item === "string" && !isNaN(item))) {
            ids.add(String(item).trim());
          } else if (typeof item === "string") {
            names.add(item.trim().toLowerCase());
          }
        });
      } else if (typeof tlProp === "object") {
        if (tlProp.id != null) ids.add(String(tlProp.id).trim());
        if (tlProp.name) names.add(String(tlProp.name).trim().toLowerCase());
      } else if (typeof tlProp === "number") {
        ids.add(String(tlProp).trim());
      } else if (typeof tlProp === "string") {
        const trimmed = tlProp.trim();
        if (!isNaN(trimmed) && trimmed !== "") {
          ids.add(trimmed);
        } else {
          names.add(trimmed.toLowerCase());
        }
      }
    }

    return { ids, names };
  };

  const { ids: tlIds, names: tlNames } = extractTLInfos(job.team_leader, job.team_leader_id);

  if (currentUserId && tlIds.has(currentUserId)) {
    return true;
  }

  if (currentUserName) {
    for (const name of tlNames) {
      if (name === currentUserName || name.includes(currentUserName) || currentUserName.includes(name)) {
        return true;
      }
    }
  }

  return false;
}

function resolveStaffList(assignedStaff, staffDetails, globalStaffList = []) {
  const staffMap = {};
  (globalStaffList || []).forEach((s) => {
    if (s && s.id) staffMap[String(s.id)] = s.name;
  });

  if (staffDetails) {
    const list = Array.isArray(staffDetails) ? staffDetails : [staffDetails];
    const resolved = [];
    list.forEach((s) => {
      if (!s) return;
      const cName = cleanStaffName(s.name, globalStaffList);
      if (cName && !cName.startsWith("Staff #")) {
        resolved.push({ id: s.id, name: cName });
      }
    });
    if (resolved.length > 0) return resolved;
  }

  let ids = [];
  if (Array.isArray(assignedStaff)) {
    ids = assignedStaff;
  } else if (typeof assignedStaff === "number") {
    ids = [assignedStaff];
  } else if (typeof assignedStaff === "string") {
    const trimmed = assignedStaff.trim();
    if (trimmed.startsWith("[")) {
      try {
        ids = JSON.parse(trimmed);
      } catch (e) {
        ids = trimmed.replace(/[[\]]/g, "").split(",").map((s) => s.trim()).filter(Boolean);
      }
    } else if (trimmed !== "") {
      ids = trimmed.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }

  const result = [];
  (Array.isArray(ids) ? ids : [ids]).forEach((idVal) => {
    if (typeof idVal === "object" && idVal !== null) {
      const cName = cleanStaffName(idVal.name, globalStaffList) || staffMap[String(idVal.id)] || `Staff #${idVal.id}`;
      result.push({ id: idVal.id, name: cName });
    } else {
      const key = String(idVal);
      const name = staffMap[key] || (globalStaffList.length === 1 ? globalStaffList[0].name : null);
      if (name) {
        result.push({ id: idVal, name });
      }
    }
  });

  if (result.length > 0) {
    return result;
  }

  if (globalStaffList && globalStaffList.length > 0) {
    return globalStaffList;
  }

  return [];
}

function normalizeJobDetails(raw) {
  const jobRaw = raw.job || {};
  const staff = raw.assigned_staff || raw.job?.assigned_staff || [];

  const photoRowsByArticle = {};
  (raw.photo_rows || []).forEach((pr) => {
    photoRowsByArticle[pr.article_id] = pr;
  });

  const articles = (raw.articles || []).map((a) => {
    const pr = photoRowsByArticle[a.id] || {};
    const assignedStaffList = resolveStaffList(a.assigned_staff, a.assigned_staff_details, staff);
    const assignedStaffNames = assignedStaffList.map((s) => s.name).join(", ");

    const activityBefore = toPhotoList(pr.activity_before_photos);
    if (activityBefore.length === 0 && (pr.before_photo || pr.activity_before_photo)) {
      const fallback = pr.before_photo || pr.activity_before_photo;
      activityBefore.push({ id: null, url: toPhotoUrl(fallback), filename: fallback });
    }

    const siteActivities = (a.site_activities || []).map((r) => ({
      id: r.id,
      activity_type: r.activity_type,
      start: r.start || r.start_time || "",
      finish: r.finish || r.end_time || "",
      self_remark: r.self_remark || "",
      team_leader_remark: r.team_leader_remark || "",
      supervisor_remark: r.supervisor_remark || "",
      manager_remark: r.manager_remark || "",
      field_staff_id: r.field_staff_id || null,
      field_staff_name: cleanStaffName(r.field_staff_name, staff) || assignedStaffNames || null,
      before_photo_url: r.before_photo ? toPhotoUrl(r.before_photo) : null,
      before_photo_file: null,
      after_photo_url: r.after_photo ? toPhotoUrl(r.after_photo) : null,
      after_photo_file: null,
    }));

    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserRole = (currentUser?.role || "").toLowerCase().replace(/_/g, " ");
    const isFieldStaffUser = ["field staff", "field_staff", "staff"].includes(currentUserRole);

    const reachedEntries = a.reached_site_entries || [];
    let staffForReached = (staff && staff.length > 0)
      ? staff
      : (assignedStaffList && assignedStaffList.length > 0)
        ? assignedStaffList
        : [];

    if (isFieldStaffUser && currentUser?.id) {
      staffForReached = staffForReached.filter((s) => Number(s.id) === Number(currentUser.id));
    }

    let reachedSite = [];
    if (staffForReached.length > 0) {
      reachedSite = staffForReached.map((s) => {
        const foundRS = reachedEntries.find((rs) => Number(rs.staff_id) === Number(s.id));
        return {
          staff_id: s.id,
          staff_name: s.name,
          start: foundRS?.start || "",
          finish: foundRS?.finish || "",
          self_remark: foundRS?.self_remark || "",
          team_leader_remark: foundRS?.team_leader_remark || "",
          supervisor_remark: foundRS?.supervisor_remark || "",
          manager_remark: foundRS?.manager_remark || "",
        };
      });
    } else if (reachedEntries.length > 0) {
      reachedSite = reachedEntries.map((rs) => ({
        staff_id: rs.staff_id,
        staff_name: rs.staff_name || (rs.staff_id ? `Staff #${rs.staff_id}` : "Staff Member"),
        start: rs.start || "",
        finish: rs.finish || "",
        self_remark: rs.self_remark || "",
        team_leader_remark: rs.team_leader_remark || "",
        supervisor_remark: rs.supervisor_remark || "",
        manager_remark: rs.manager_remark || "",
      }));
    } else {
      reachedSite = [
        {
          staff_id: pr.reached_field_staff_id ?? null,
          staff_name: cleanStaffName(pr.reached_field_staff_name, staff) || "Staff Member",
          start: pr.reached_start_time || "",
          finish: pr.reached_end_time || "",
          self_remark: pr.reached_self_remark || "",
          team_leader_remark: pr.reached_team_leader_remark || "",
          supervisor_remark: pr.reached_supervisor_remark || "",
          manager_remark: pr.reached_manager_remark || pr.manager_remark || "",
        },
      ];
    }

    return {
      id: a.id,
      code: a.article,
      name: "",
      location: a.location,
      job_type: a.type,
      description: a.description,
      initial_photo_url: toPhotoUrl(a.initial_photo || a.initial_photo_url || a.photo || a.initial_photo_path || a.photo_url),
      est_time: a.est_time,
      status_label: a.status_label || null,
      materials: (a.materials || []).map((m) => ({ code: m.material, qty: m.qty, unit: m.unit })),
      team_leader_remark: a.team_leader_remarks,
      supervisor_remark: a.supervisor_remarks,
      // Activities-tab state
      activity: {
        reached_site: reachedSite,
        photo_row_id: pr.id ?? null,
        before_photo: {
          id: activityBefore[0]?.id ?? null,
          url: activityBefore[0]?.url ?? null,
          filename: activityBefore[0]?.filename ?? null,
          start: pr.before_start_time || "",
          finish: pr.before_end_time || "",
          self_remark: pr.self_remark || "",
          team_leader_remark: pr.team_leader_remarks || "",
          supervisor_remark: pr.supervisor_remarks || "",
          manager_remark: pr.before_manager_remark || pr.manager_remark || "",
        },
        site_activities: siteActivities,
        // FIX: reload existing after-photos from the row (previously always []).
        after_photos: toPhotoList(pr.activity_after_photos),
        after_photo_meta: {
          self_remark: pr.after_self_remark || (pr.activity_after_photos?.length ? pr.self_remark : "") || "",
          team_leader_remark: pr.after_team_leader_remarks || (pr.activity_after_photos?.length ? pr.team_leader_remarks : "") || "",
          supervisor_remark: pr.after_supervisor_remarks || (pr.activity_after_photos?.length ? pr.supervisor_remarks : "") || "",
          manager_remark: pr.after_manager_remark || "",
          start: pr.after_start_time || "",
          finish: pr.after_end_time || "",
        },
        // FIX: reload cleanup / left-site instead of always starting blank.
        cleanup: {
          start: pr.cleanup_start_time || "",
          finish: pr.cleanup_end_time || "",
          self_remark: pr.cleanup_self_remark || "",
          team_leader_remark: pr.cleanup_team_leader_remarks || "",
          supervisor_remark: pr.cleanup_supervisor_remarks || "",
          manager_remark: pr.cleanup_manager_remark || "",
        },
        left_site: {
          start: pr.left_start_time || "",
          finish: pr.left_end_time || "",
          self_remark: pr.left_self_remark || "",
          team_leader_remark: pr.left_team_leader_remarks || "",
          supervisor_remark: pr.left_supervisor_remarks || "",
          manager_remark: pr.left_manager_remark || "",
        },
      },
      // Photos-tab state (independent gallery, same DB row, different columns)
      photos: {
        photo_row_id: pr.id ?? null,
        job_card_photo_url: toPhotoUrl(pr.job_card_photo),
        job_card_photo_file: null,
        before: toPhotoList(pr.gallery_before_photos),
        after: toPhotoList(pr.gallery_after_photos),
        deleted_photo_ids: [],
        self_remark: pr.self_remark,
        team_leader_remark: pr.team_leader_remarks,
        supervisor_remark: pr.supervisor_remarks,
      },
    };
  });

  const materials_issued = (raw.issued_materials || raw.materials_issued || []).map((m) => ({
    id: m.id,
    material_code: m.material_name,
    issued_qty: m.issued_qty,
    unit: m.unit,
    used_qty: m.used,
    return_qty: m.return_qty,
    manual_override: false,
    remarks: m.remarks,
    is_extra: false,
  }));

  return {
    id: jobRaw.id,
    job_no: jobRaw.job_no,
    client_name: jobRaw.client_name,
    location: jobRaw.location,
    address: jobRaw.address,
    status: jobRaw.status,
    staff,
    team_leader: raw.team_leader || (jobRaw.team_leader_id ? (staff.find((s) => String(s.id) === String(jobRaw.team_leader_id)) || { id: jobRaw.team_leader_id, name: `Staff #${jobRaw.team_leader_id}` }) : null),
    team_leader_id: jobRaw.team_leader_id || raw.team_leader?.id || (typeof raw.team_leader === "object" ? raw.team_leader?.id : null),
    site_activity_options: raw.site_activity_options || null,
    articles,
    materials_issued,
  };
}

// Fallback reference list — only used if the API does not send site_activity_options.
const DEFAULT_SITE_ACTIVITY_OPTIONS = [
  "Office Briefing", "Material Collection / Return", "Travelling / Transporting",
  "In house Work", "Cutting", "Wrapping", "Cleaning Surface", "Priming",
  "Installing", "Measuring", "Inspection",
];

function badgeClass(status) {
  const s = (status || "").toLowerCase();
  return `jd-badge jd-badge-${s || "pending"}`;
}

function rowStatus(start, finish) {
  if (finish) return { label: "Completed", cls: "jd-st-done" };
  if (start) return { label: "In Progress", cls: "jd-st-progress" };
  return { label: "Not Started", cls: "jd-st-pending" };
}

function computeArticleStatus(article) {
  const act = article?.activity || {};
  const siteActivities = act.site_activities || [];
  const reachedRows = act.reached_site || [];
  const beforePhoto = act.before_photo || {};
  const afterPhotos = act.after_photos || [];
  const afterMeta = act.after_photo_meta || {};
  const cleanup = act.cleanup || {};
  const leftSite = act.left_site || {};

  const statuses = [];

  // Reached Site
  if (reachedRows.some((r) => r.finish)) statuses.push("completed");
  else if (reachedRows.some((r) => r.start)) statuses.push("in_progress");
  else statuses.push("not_started");

  // Before Photo
  if (beforePhoto.finish || beforePhoto.url || beforePhoto.filename) statuses.push("completed");
  else if (beforePhoto.start) statuses.push("in_progress");
  else statuses.push("not_started");

  // Site Activities
  siteActivities.forEach((sa) => {
    if (sa.finish) statuses.push("completed");
    else if (sa.start) statuses.push("in_progress");
    else statuses.push("not_started");
  });

  // Cleanup
  if (cleanup.finish) statuses.push("completed");
  else if (cleanup.start) statuses.push("in_progress");

  // Left Site
  if (leftSite.finish) statuses.push("completed");
  else if (leftSite.start) statuses.push("in_progress");

  // After Photos
  if (afterPhotos.length > 0 || afterMeta.finish) statuses.push("completed");
  else if (afterMeta.start) statuses.push("in_progress");
  else statuses.push("not_started");

  const hasCompleted = statuses.includes("completed");
  const hasInProgress = statuses.includes("in_progress");
  const hasNotStarted = statuses.includes("not_started");

  if (hasInProgress || (hasCompleted && hasNotStarted)) {
    return { label: "In Progress", cls: "jd-status-progress" };
  }

  if (hasCompleted && !hasNotStarted && !hasInProgress) {
    return { label: "Completed", cls: "jd-status-completed" };
  }

  return { label: "Not Started", cls: "jd-status-notstarted" };
}

function computeOverallJobStatus(job) {
  if (!job || !job.articles || job.articles.length === 0) {
    return job?.status || "Ongoing";
  }

  const articleStatuses = job.articles.map((a) => computeArticleStatus(a));

  const allCompleted = articleStatuses.every((s) => s.label === "Completed");
  const anyInProgress = articleStatuses.some((s) => s.label === "In Progress" || s.label === "Completed");
  const allNotStarted = articleStatuses.every((s) => s.label === "Not Started");

  if (allCompleted) {
    return "Completed";
  }
  if (anyInProgress) {
    return "Ongoing";
  }
  if (allNotStarted) {
    return "Pending";
  }
  return job.status || "Ongoing";
}

/* ─────────────────────────── Lightbox ─────────────────────────── */
function Lightbox({ src, filename, onClose }) {
  const candidates = getCandidatePhotoUrls(src || filename);
  const [srcIndex, setSrcIndex] = useState(0);

  useEffect(() => {
    setSrcIndex(0);
  }, [src, filename]);

  if (!src) return null;

  const currentSrc = candidates[srcIndex] || BROKEN_IMG_PLACEHOLDER;
  const isBroken = srcIndex >= candidates.length;

  return (
    <div className="jd-lightbox jd-open" onClick={onClose}>
      <button className="jd-lightbox-close" onClick={onClose} aria-label="Close preview">✕</button>
      <div onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
        {isBroken ? (
          <div style={{ padding: "24px 40px", background: "#ffffff", borderRadius: 12, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}>
            <img src={BROKEN_IMG_PLACEHOLDER} alt="broken" style={{ width: 80, height: 80, opacity: 0.6, marginBottom: 10 }} />
            <p style={{ margin: 0, fontSize: "0.9rem", color: "#64748b", fontWeight: 500 }}>Photo file not found on server</p>
          </div>
        ) : (
          <img
            src={currentSrc}
            alt="preview"
            onError={() => {
              if (srcIndex < candidates.length - 1) {
                setSrcIndex((prev) => prev + 1);
              } else {
                setSrcIndex(candidates.length);
              }
            }}
          />
        )}
      </div>
      <div className="jd-lightbox-actions" onClick={(e) => e.stopPropagation()}>
        {!isBroken && (
          <button
            type="button"
            className="jd-download-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 18px",
              borderRadius: 8,
              background: "#4f46e5",
              color: "#ffffff",
              border: "none",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.9rem",
              boxShadow: "0 2px 8px rgba(79, 70, 229, 0.3)"
            }}
            onClick={() => downloadImage(currentSrc, filename || "photo.jpg")}
          >
            ⬇ Download Photo
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── Photo Image Component ─────────────────────────── */
function PhotoImg({ src, filename, alt, className, style }) {
  const candidates = getCandidatePhotoUrls(src || filename);
  const [srcIndex, setSrcIndex] = useState(0);

  useEffect(() => setSrcIndex(0), [src, filename]);

  const isBroken = srcIndex >= candidates.length;
  const currentSrc = isBroken ? BROKEN_IMG_PLACEHOLDER : (candidates[srcIndex] || BROKEN_IMG_PLACEHOLDER);

  return (
    <img
      src={currentSrc}
      alt={alt || "photo"}
      className={className}
      style={style}
      onError={() => {
        if (srcIndex < candidates.length - 1) {
          setSrcIndex((prev) => prev + 1);
        } else {
          setSrcIndex(candidates.length);
        }
      }}
    />
  );
}

/* ─────────────────────────── Photo upload slot ─────────────────────────── */
const BROKEN_IMG_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44"><rect width="44" height="44" fill="#F3F4F6"/><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" transform="translate(9,9)" fill="none" stroke="#D1D5DB" stroke-width="1.8"/><circle cx="21" cy="22" r="4" fill="none" stroke="#D1D5DB" stroke-width="1.8"/></svg>'
  );

function PhotoUploadSlot({ image, onUpload, onRemove, disabled, hint }) {
  const inputRef = useRef(null);
  const candidates = getCandidatePhotoUrls(image);
  const [srcIndex, setSrcIndex] = useState(0);

  useEffect(() => setSrcIndex(0), [image]);

  const isBroken = srcIndex >= candidates.length;
  const showImage = Boolean(image) && !isBroken;
  const currentSrc = candidates[srcIndex] || "";

  return (
    <div className="jd-upload-wrap">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (ev) => onUpload(ev.target.result, file.name, file);
          reader.readAsDataURL(file);
          e.target.value = "";
        }}
      />
      <div
        className={`jd-upload-box ${showImage ? "jd-has-img" : ""} ${disabled ? "jd-disabled" : ""}`}
        title={showImage ? "Click to change photo" : (isBroken ? "Photo failed to load — click to re-upload" : undefined)}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        {showImage ? (
          <img
            src={currentSrc}
            alt="preview"
            onError={() => {
              if (srcIndex < candidates.length - 1) {
                setSrcIndex((prev) => prev + 1);
              } else {
                setSrcIndex(candidates.length);
              }
            }}
          />
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={disabled ? "#c7d2fe" : "#818cf8"} strokeWidth="1.8">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        )}
      </div>
      {showImage && onRemove && !disabled && (
        <button
          type="button"
          className="jd-photo-remove-btn"
          title="Remove photo"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onRemove();
          }}
        >
          ✕
        </button>
      )}
      {hint && !showImage && <span className="jd-upload-hint">{hint}</span>}
    </div>
  );
}


function formatDatetimeLocal(val) {
  if (!val) return "";
  const str = String(val).trim();
  if (!str) return "";
  if (str.length === 16 && str.includes("T")) return str;
  if (str.length >= 19 && str.includes(" ")) {
    return str.substring(0, 19).replace(" ", "T").substring(0, 16);
  }
  try {
    const d = new Date(str.includes("T") ? str : str.replace(" ", "T"));
    if (isNaN(d.getTime())) return str;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (e) {
    return str;
  }
}

function computeDuration(start, finish) {
  if (!start || !finish) return null;
  try {
    const sStr = String(start).trim();
    const fStr = String(finish).trim();
    if (!sStr || !fStr) return null;
    const d1 = new Date(sStr.includes("T") ? sStr : sStr.replace(" ", "T"));
    const d2 = new Date(fStr.includes("T") ? fStr : fStr.replace(" ", "T"));
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return null;
    const diffMs = d2.getTime() - d1.getTime();
    if (diffMs <= 0) return null;
    const totalMins = Math.floor(diffMs / (1000 * 60));
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  } catch (e) {
    return null;
  }
}

function ActivityRow({
  sno, snoCls, label, start, finish, self, tl, sup, mgr,
  fixed, staffChip, teamLevel, onChange, removable, onRemove,
  activityCell, job, canEditStaffActivity = true, showStartEnd = false,
}) {
  const status = rowStatus(start, finish);
  const rowCls = fixed ? "jd-row-fixed" : "jd-row-site";
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const role = (user?.role || "").toLowerCase().replace(/_/g, " ").trim();

  const isAdmin = role === "admin";
  const isSupervisor = role === "supervisor";
  const isManager = role === "manager";
  const isTeamLeader = ["team leader", "team_leader", "tl", "teamleader", "leader"].includes(role) || checkIsTeamLeader(user, job);

  const canEditTL = isAdmin || isTeamLeader || isSupervisor || isManager;
  const canEditSupervisor = isAdmin || isSupervisor || isManager;
  const canEditManager = isAdmin || isManager;

  const durationStr = showStartEnd ? computeDuration(start, finish) : null;

  return (
    <tr className={`jd-act-tr ${rowCls}`}>
      <td className="jd-col-sno">
        <span className={`jd-sno ${snoCls}`}>{sno}</span>
      </td>
      <td className="jd-col-act">
        {activityCell ? activityCell : <span className="jd-row-label">{label}</span>}
      </td>
      <td className="jd-col-st">
        <span className={`jd-st-badge ${status.cls}`}>{status.label}</span>
      </td>
      <td className="jd-col-fs">
        {teamLevel ? (
          <span className="jd-fs-teamlevel">Team level</span>
        ) : staffChip ? (
          <span className="jd-fs-chip">{cleanStaffName(staffChip, []) || staffChip}</span>
        ) : (
          <span className="jd-fs-empty">—</span>
        )}
      </td>
      <td className="jd-col-date">
        {showStartEnd ? (
          <div className="jd-date-range-box" style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 165 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, minWidth: 28, textTransform: "uppercase" }}>Start</span>
              <input
                type="datetime-local"
                className="jd-input"
                style={{ fontSize: "0.78rem", padding: "2px 5px" }}
                disabled={!canEditStaffActivity}
                value={formatDatetimeLocal(start)}
                onChange={(e) => onChange?.("start", e.target.value)}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, minWidth: 28, textTransform: "uppercase" }}>End</span>
              <input
                type="datetime-local"
                className="jd-input"
                style={{ fontSize: "0.78rem", padding: "2px 5px" }}
                disabled={!canEditStaffActivity}
                value={formatDatetimeLocal(finish)}
                onChange={(e) => onChange?.("finish", e.target.value)}
              />
            </div>
            {durationStr && (
              <div style={{ fontSize: "0.7rem", color: "#4f46e5", fontWeight: 600, paddingLeft: 32 }}>
                ⏱ Productivity: {durationStr}
              </div>
            )}
          </div>
        ) : (
          <input
            type="datetime-local"
            className="jd-input"
            disabled={!canEditStaffActivity}
            value={formatDatetimeLocal(start || finish)}
            onChange={(e) => {
              const val = e.target.value;
              onChange?.("datetime", val);
            }}
          />
        )}
      </td>
      <td className="jd-col-remark">
        <input
          type="text"
          className="jd-input"
          disabled={!canEditStaffActivity}
          placeholder={canEditStaffActivity ? "Self remark..." : "Assigned staff only"}
          value={self || ""}
          onChange={(e) => onChange?.("self_remark", e.target.value)}
        />
      </td>
      <td className="jd-col-remark">
        <input
          type="text"
          className="jd-input"
          disabled={!canEditTL}
          placeholder={canEditTL ? "TL remark..." : "TL / Admin only"}
          value={tl || ""}
          onChange={(e) => onChange?.("team_leader_remark", e.target.value)}
        />
      </td>
      <td className="jd-col-remark">
        <input
          type="text"
          className="jd-input"
          disabled={!canEditSupervisor}
          placeholder={canEditSupervisor ? "Supervisor remark..." : "Supervisor / Admin only"}
          value={sup || ""}
          onChange={(e) => onChange?.("supervisor_remark", e.target.value)}
        />
      </td>
      <td className="jd-col-mgr">
        <div className="jd-mgr-wrap">
          <input
            type="text"
            className="jd-input"
            disabled={!canEditManager}
            placeholder={canEditManager ? "Manager remark..." : "Manager / Admin only"}
            value={mgr || ""}
            onChange={(e) => onChange?.("manager_remark", e.target.value)}
          />
          {removable && canEditStaffActivity && (
            <button className="jd-remove-row-btn" title="Remove activity" onClick={onRemove}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ─────────────────────────── Article accordion item ─────────────────────────── */
function ArticleAccordion({
    article,
    index,
    isOpen,
    onToggle,
    activityOptions,
    onUpdateArticle,
    onOpenLightbox,
    saveActivity,
    savingActivity,
    job,
}) {
  const act = article?.activity || {};
  const beforePhoto = act.before_photo || {};
  const afterPhotos = act.after_photos || [];
  const afterMeta = act.after_photo_meta || {};
  const siteActivities = act.site_activities || [];
  const cleanup = act.cleanup || {};
  const leftSite = act.left_site || {};

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const role = (user?.role || "").toLowerCase().replace("_", " ");
  const userId = user.id != null ? String(user.id).trim() : (user.user_id != null ? String(user.user_id).trim() : null);
  const userName = user.name ? String(user.name).trim().toLowerCase() : "";

  const isAdmin = role === "admin";
  const isTeamLeader = checkIsTeamLeader(user, job);
  const isSupervisor = role === "supervisor";
  const isManager = role === "manager";
  const isFieldStaff = !isAdmin && !isTeamLeader && !isSupervisor && !isManager;

  const reachedRowsRaw = act.reached_site?.length
    ? act.reached_site
    : (article.assigned_staff || []).map((s) => ({ staff_id: s.id, staff_name: s.name, start: "", finish: "" }));

  const reachedRows = (isFieldStaff && user?.id)
    ? reachedRowsRaw.filter((r) => !r.staff_id || String(r.staff_id) === String(user.id))
    : reachedRowsRaw;

  const patchActivity = (patch) => onUpdateArticle(article.id, { activity: { ...act, ...patch } });
  const patchReached = (targetItemOrIdx, field, value) => {
    const next = [...reachedRowsRaw];
    let realIdx = typeof targetItemOrIdx === "number" ? targetItemOrIdx : -1;
    if (typeof targetItemOrIdx === "object" && targetItemOrIdx !== null) {
      realIdx = next.findIndex((r) => (r.staff_id ? String(r.staff_id) === String(targetItemOrIdx.staff_id) : r === targetItemOrIdx));
    }
    if (realIdx === -1) return;
    if (field === "datetime") {
      next[realIdx] = { ...next[realIdx], start: value, finish: value };
    } else {
      next[realIdx] = { ...next[realIdx], [field]: value };
    }
    patchActivity({ reached_site: next });
  };
  const patchSite = (targetItem, fieldOrObject, value) => {
    const realIdx = siteActivities.findIndex((a) => (a.id && targetItem?.id ? a.id === targetItem.id : a === targetItem));
    if (realIdx === -1) return;
    const next = [...siteActivities];
    if (typeof fieldOrObject === "object") {
      next[realIdx] = { ...next[realIdx], ...fieldOrObject };
    } else {
      next[realIdx] = { ...next[realIdx], [fieldOrObject]: value };
    }
    patchActivity({ site_activities: next });
  };
  const addSiteRow = () => patchActivity({
    site_activities: [
      ...siteActivities,
      {
        id: `new-${Date.now()}`,
        activity_type: "",
        start: "",
        finish: "",
        field_staff_id: user?.id ?? null,
        field_staff_name: user?.name || user?.email || null,
      },
    ],
  });
  const removeSiteRow = (targetItem) => {
    const next = siteActivities.filter((a) => (a.id && targetItem?.id ? a.id !== targetItem.id : a !== targetItem));
    patchActivity({ site_activities: next });
  };

  const materials = (article.materials || []).slice(0, 3);

  let snoCounter = 0;
  const nextSno = () => ++snoCounter;

  const beforeStatus = rowStatus(beforePhoto.start, beforePhoto.finish);
  const afterStatus = rowStatus(afterPhotos[0]?.url, afterPhotos[1]?.url);
  const articleStatus = computeArticleStatus(article);

  // Item 3: Field staff see only their own activities; supervisors, managers & TLs see all
  const visibleSiteActivities = (isFieldStaff && user?.id)
    ? siteActivities.filter((r) => {
        if (!r.field_staff_id && !r.field_staff_name) return true;
        if (r.field_staff_id && String(r.field_staff_id) === String(user.id)) return true;
        if (r.field_staff_name && user.name && r.field_staff_name.toLowerCase().includes(user.name.toLowerCase())) return true;
        return false;
      })
    : siteActivities;



  const canEditTL = isAdmin || isTeamLeader || isSupervisor || isManager;
  const canEditSupervisor = isAdmin || isSupervisor || isManager;
  const canEditManager = isAdmin || isManager;

  const canEditStaffActivity = true;

  return (
    <div className="jd-article-card">
      <div className="jd-article-header" onClick={onToggle}>
        <div className="jd-article-header-left">
          <div className="jd-article-num">{index + 1}</div>
          <div>
            <span className="jd-article-title">{article.name ? `${article.code} — ${article.name}` : article.code}</span>
            <span className="jd-article-meta">
              {[article.location, article.job_type, (article.assigned_staff || []).map((s) => s.name).join(", "), article.est_time]
                .filter(Boolean).join(" · ")}
            </span>
          </div>
        </div>
        <div className="jd-article-header-right">
          <span className={`jd-article-status ${articleStatus.cls}`}>
            {articleStatus.label}
          </span>
          <span className="jd-article-materials-preview">
            {materials.map((m) => `${m.code}×${m.qty}`).join(" · ")}
          </span>
          <svg className={`jd-chevron ${isOpen ? "jd-open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      <div className={`jd-article-body ${isOpen ? "jd-open" : ""}`}>
        {/* ── Article Summary / Overview Card ── */}
        <div className="jd-ov-card">
          <div className="jd-ov-grid">
            <div className="jd-ov-box">
              <span className="jd-ov-lbl">Assigned Article</span>
              <span className="jd-ov-val code">{article.code}</span>
            </div>
            <div className="jd-ov-box">
              <span className="jd-ov-lbl">Location</span>
              <span className="jd-ov-val">{article.location || "—"}</span>
            </div>
            <div className="jd-ov-box">
              <span className="jd-ov-lbl">Job Type</span>
              <span className="jd-ov-val">{article.job_type || "—"}</span>
            </div>
            <div className="jd-ov-box">
              <span className="jd-ov-lbl">Est. Time</span>
              <span className="jd-ov-val">{article.est_time || "—"}</span>
            </div>
            <div className="jd-ov-box">
              <span className="jd-ov-lbl">Assigned Staff</span>
              <span className="jd-staff-pill">{(article.assigned_staff || []).map((s) => s.name).join(", ") || "—"}</span>
            </div>
            <div className="jd-ov-box">
              <span className="jd-ov-lbl">Initial Photo</span>
              {article.initial_photo_url ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                  <PhotoImg
                    src={article.initial_photo_url}
                    alt="Initial Photo"
                    style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, cursor: "pointer", border: "1px solid #cbd5e1", background: "#f1f5f9" }}
                    onClick={() => onOpenLightbox(article.initial_photo_url, "Initial Photo")}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <button className="jd-view-link" onClick={() => onOpenLightbox(article.initial_photo_url, "Initial Photo")}>
                      View 📷
                    </button>
                    <button
                      className="jd-view-link"
                      style={{ color: "#4f46e5", fontWeight: 600 }}
                      onClick={() => downloadImage(article.initial_photo_url, `${article.code || 'article'}_initial_photo.jpg`)}
                    >
                      Download ⬇
                    </button>
                  </div>
                </div>
              ) : (
                <span className="jd-ov-val muted">—</span>
              )}
            </div>
          </div>

          {article.description && (
            <div className="jd-ov-desc">
              <span className="jd-ov-lbl">Job Description</span>
              <p>{article.description}</p>
            </div>
          )}

          <div className="jd-ov-materials">
            <span className="jd-ov-lbl">Estimated Materials (Article Wise)</span>
            <table style={{ width: "100%", marginTop: 6, fontSize: "0.82rem", borderCollapse: "collapse", background: "#f8fafc", borderRadius: 6, overflow: "hidden" }}>
              <thead>
                <tr style={{ background: "#f1f5f9", textAlign: "left", color: "#475569" }}>
                  <th style={{ padding: "6px 10px" }}>Material Name / Code</th>
                  <th style={{ padding: "6px 10px" }}>Est. Qty</th>
                  <th style={{ padding: "6px 10px" }}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {materials.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: "8px 10px", color: "#94a3b8", fontStyle: "italic" }}>No estimated materials assigned for this article</td></tr>
                ) : (
                  materials.map((m, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "6px 10px", fontWeight: 500, color: "#1e293b" }}>{m.code || m.material_name || m.name || m.material || "—"}</td>
                      <td style={{ padding: "6px 10px", color: "#334155" }}>{m.qty || m.quantity || 1}</td>
                      <td style={{ padding: "6px 10px", color: "#64748b" }}>{m.unit || "pcs"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="jd-ov-remarks">
            <div className="jd-m-fg">
              <label className="jd-m-lbl">Team Leader Remark 👑</label>
              <input
                type="text"
                className="jd-input"
                disabled={!canEditTL}
                placeholder={canEditTL ? "Team leader remark..." : "TL / Admin only"}
                value={article.team_leader_remark || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  onUpdateArticle(article.id, {
                    team_leader_remark: val,
                  });
                }}
              />
            </div>
            <div className="jd-m-fg">
              <label className="jd-m-lbl">Supervisor Remark</label>
              <input
                type="text"
                className="jd-input"
                disabled={!canEditSupervisor}
                placeholder={canEditSupervisor ? "Supervisor remark..." : "Supervisor / Admin only"}
                value={article.supervisor_remark || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  onUpdateArticle(article.id, {
                    supervisor_remark: val,
                  });
                }}
              />
            </div>
          </div>
        </div>

        {/* ── Activity Section Header ── */}
        <div className="jd-activity-hd">
          <div className="jd-activity-hd-inner">
            <div className="jd-activity-hd-line" />
            <span className="jd-activity-hd-tag">Assigned Article {article.code} Activity</span>
            <div className="jd-activity-hd-line" />
          </div>
        </div>

        {/* ── Neat Table with Column Headers ── */}
        <div className="jd-act-table-wrap">
          <table className="jd-act-table">
            <thead>
              <tr>
                <th style={{ width: 45, minWidth: 45 }}>S.No</th>
                <th style={{ minWidth: 200 }}>Site Activity</th>
                <th style={{ minWidth: 105 }}>Status</th>
                <th style={{ minWidth: 120 }}>Field Staff</th>
                <th style={{ minWidth: 180 }}>Date &amp; Time</th>
                <th style={{ minWidth: 160 }}>Self Remark</th>
                <th style={{ minWidth: 160 }}>Team Leader 👑</th>
                <th style={{ minWidth: 160 }}>Supervisor</th>
                <th style={{ minWidth: 160 }}>Manager</th>
              </tr>
            </thead>
            <tbody>
              {/* A. Reached Site At */}
              {reachedRows.map((r, idx) => {
                const canEditRowStaff = canEditStaffActivity || (userId && r.staff_id != null && String(r.staff_id).trim() === userId) || (userName && r.staff_name && r.staff_name.toLowerCase().includes(userName));
                return (
                  <ActivityRow
                    key={r.staff_id ?? idx}
                    sno={reachedRows.length > 1 ? `A${idx + 1}` : "A"}
                    snoCls="jd-sno-fixed"
                    label="Reached Site at"
                    fixed
                    start={r.start} finish={r.finish}
                    self={r.self_remark} tl={r.team_leader_remark} sup={r.supervisor_remark} mgr={r.manager_remark}
                    staffChip={r.staff_name}
                    onChange={(field, value) => patchReached(r, field, value)}
                    job={job}
                    canEditStaffActivity={canEditRowStaff}
                  />
                );
              })}

              {/* Before Photo Row */}
              <tr className="jd-act-tr jd-row-photo">
                <td className="jd-col-sno">
                  <span className="jd-sno jd-sno-photo">{nextSno()}</span>
                </td>
                <td className="jd-col-act">
                  <div className="jd-photo-cell-inner">
                    <span className="jd-row-label">Before Photo</span>
                    <PhotoUploadSlot
                      image={beforePhoto.url}
                      hint="Upload photo"
                      disabled={!canEditStaffActivity}
                      onUpload={(dataUrl, fileName, file) =>
                        patchActivity({
                          before_photo: {
                            ...beforePhoto,
                            url: dataUrl,
                            filename: fileName,
                            file: file,
                          },
                        })
                      }
                      onRemove={() =>
                        patchActivity({
                          before_photo: {
                            ...beforePhoto,
                            url: null,
                            filename: null,
                            file: null,
                          },
                        })
                      }
                    />
                  </div>
                </td>
                <td className="jd-col-st">
                  <span className={`jd-st-badge ${beforeStatus.cls}`}>{beforeStatus.label}</span>
                </td>
                <td className="jd-col-fs">
                  <span className="jd-fs-empty">—</span>
                </td>
                <td className="jd-col-date">
                  <input
                    type="datetime-local"
                    className="jd-input"
                    disabled={!canEditStaffActivity}
                    value={formatDatetimeLocal(beforePhoto.start || beforePhoto.finish)}
                    onChange={(e) => patchActivity({ before_photo: { ...beforePhoto, start: e.target.value, finish: e.target.value } })}
                  />
                </td>
                <td className="jd-col-remark">
                  <input
                    type="text"
                    className="jd-input"
                    disabled={!canEditStaffActivity}
                    placeholder={canEditStaffActivity ? "Self remark..." : "Assigned staff only"}
                    value={beforePhoto.self_remark || ""}
                    onChange={(e) => patchActivity({ before_photo: { ...beforePhoto, self_remark: e.target.value } })}
                  />
                </td>
                <td className="jd-col-remark">
                  <input type="text" className="jd-input" disabled={!canEditTL} placeholder={canEditTL ? "TL remark..." : "TL / Admin only"} value={beforePhoto.team_leader_remark || ""} onChange={(e) => patchActivity({ before_photo: { ...beforePhoto, team_leader_remark: e.target.value } })} />
                </td>
                <td className="jd-col-remark">
                  <input type="text" className="jd-input" disabled={!canEditSupervisor} placeholder={canEditSupervisor ? "Supervisor remark..." : "Supervisor / Admin only"} value={beforePhoto.supervisor_remark || ""} onChange={(e) => patchActivity({ before_photo: { ...beforePhoto, supervisor_remark: e.target.value } })} />
                </td>
                <td className="jd-col-mgr">
                  <input type="text" className="jd-input" disabled={!canEditManager} placeholder={canEditManager ? "Manager remark..." : "Manager / Admin only"} value={beforePhoto.manager_remark || ""} onChange={(e) => patchActivity({ before_photo: { ...beforePhoto, manager_remark: e.target.value } })} />
                </td>
              </tr>

              {/* Dynamic Site Activities */}
              {visibleSiteActivities.map((r, idx) => {
                const canEditRowStaff = canEditStaffActivity || (userId && r.field_staff_id != null && String(r.field_staff_id).trim() === userId) || (userName && r.field_staff_name && r.field_staff_name.toLowerCase().includes(userName));
                return (
                  <ActivityRow
                    key={r.id ?? idx}
                    sno={nextSno()}
                    snoCls="jd-sno-site"
                    fixed={false}
                    showStartEnd={true}
                    start={r.start} finish={r.finish}
                    self={r.self_remark} tl={r.team_leader_remark} sup={r.supervisor_remark} mgr={r.manager_remark}
                    staffChip={r.field_staff_name}
                    removable={canEditRowStaff}
                    onRemove={() => removeSiteRow(r)}
                    onChange={(field, value) => patchSite(r, field, value)}
                    job={job}
                    canEditStaffActivity={canEditRowStaff}
                    activityCell={
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <SearchableSelect
                          options={activityOptions}
                          value={r.activity_type || ""}
                          onChange={(val) => patchSite(r, "activity_type", val)}
                          placeholder="— Select Site Activity —"
                          searchPlaceholder="Search activity..."
                          disabled={!canEditRowStaff}
                          style={{ minWidth: 150 }}
                        />
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <PhotoUploadSlot
                            image={r.before_photo_url}
                            hint="Before"
                            disabled={!canEditRowStaff}
                            onUpload={(dataUrl, fileName, file) =>
                              patchSite(r, { before_photo_url: dataUrl, before_photo_file: file })
                            }
                            onRemove={() =>
                              patchSite(r, { before_photo_url: null, before_photo_file: null })
                            }
                          />
                          <PhotoUploadSlot
                            image={r.after_photo_url}
                            hint="After"
                            disabled={!canEditRowStaff}
                            onUpload={(dataUrl, fileName, file) =>
                              patchSite(r, { after_photo_url: dataUrl, after_photo_file: file })
                            }
                            onRemove={() =>
                              patchSite(r, { after_photo_url: null, after_photo_file: null })
                            }
                          />
                        </div>
                      </div>
                    }
                  />
                );
              })}

              {canEditStaffActivity && (
                <tr className="jd-add-tr">
                  <td colSpan={9}>
                    <button className="jd-add-site-btn" onClick={addSiteRow}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add Extra Site Activity
                    </button>
                  </td>
                </tr>
              )}

              {/* Finished & After Photo Row */}
              <tr className="jd-act-tr jd-row-photo">
                <td className="jd-col-sno">
                  <span className="jd-sno jd-sno-photo">{nextSno()}</span>
                </td>
                <td className="jd-col-act">
                  <div className="jd-photo-cell-inner">
                    <span className="jd-row-label">Finished &amp; After Photo</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[0, 1].map((i) => (
                        <PhotoUploadSlot
                          key={i}
                          image={afterPhotos[i]?.url}
                          disabled={!canEditStaffActivity}
                          onUpload={(dataUrl, fileName, file) => {
                            const next = [...afterPhotos];
                            next[i] = { url: dataUrl, filename: fileName, file: file };
                            patchActivity({ after_photos: next });
                          }}
                          onRemove={() => {
                            const next = [...afterPhotos];
                            next[i] = { url: null, filename: null, file: null };
                            patchActivity({ after_photos: next });
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </td>
                <td className="jd-col-st">
                  <span className={`jd-st-badge ${afterStatus.cls}`}>{afterStatus.label}</span>
                </td>
                <td className="jd-col-fs">
                  <span className="jd-fs-empty">—</span>
                </td>
                <td className="jd-col-date">
                  <input
                    type="datetime-local"
                    className="jd-input"
                    disabled={!canEditStaffActivity}
                    value={formatDatetimeLocal(afterMeta.start || afterMeta.finish)}
                    onChange={(e) => patchActivity({ after_photo_meta: { ...afterMeta, start: e.target.value, finish: e.target.value } })}
                  />
                </td>
                <td className="jd-col-remark">
                  <input
                    type="text"
                    className="jd-input"
                    disabled={!canEditStaffActivity}
                    placeholder={canEditStaffActivity ? "Self remark..." : "Assigned staff only"}
                    value={afterMeta.self_remark || ""}
                    onChange={(e) => patchActivity({ after_photo_meta: { ...afterMeta, self_remark: e.target.value } })}
                  />
                </td>
                <td className="jd-col-remark">
                  <input type="text" className="jd-input" disabled={!canEditTL} placeholder={canEditTL ? "TL remark..." : "TL / Admin only"} value={afterMeta.team_leader_remark || ""} onChange={(e) => patchActivity({ after_photo_meta: { ...afterMeta, team_leader_remark: e.target.value } })} />
                </td>
                <td className="jd-col-remark">
                  <input type="text" className="jd-input" disabled={!canEditSupervisor} placeholder={canEditSupervisor ? "Supervisor remark..." : "Supervisor / Admin only"} value={afterMeta.supervisor_remark || ""} onChange={(e) => patchActivity({ after_photo_meta: { ...afterMeta, supervisor_remark: e.target.value } })} />
                </td>
                <td className="jd-col-mgr">
                  <input type="text" className="jd-input" disabled={!canEditManager} placeholder={canEditManager ? "Manager remark..." : "Manager / Admin only"} value={afterMeta.manager_remark || ""} onChange={(e) => patchActivity({ after_photo_meta: { ...afterMeta, manager_remark: e.target.value } })} />
                </td>
              </tr>

              <ActivityRow
                sno="B" snoCls="jd-sno-fixed" label="Site Clean up & Pack up" fixed
                start={cleanup.start} finish={cleanup.finish}
                self={cleanup.self_remark} tl={cleanup.team_leader_remark}
                sup={cleanup.supervisor_remark} mgr={cleanup.manager_remark}
                onChange={(field, value) => {
                  if (field === "datetime") {
                    patchActivity({ cleanup: { ...cleanup, start: value, finish: value } });
                  } else {
                    patchActivity({ cleanup: { ...cleanup, [field]: value } });
                  }
                }}
                job={job}
                canEditStaffActivity={canEditStaffActivity}
              />

              <ActivityRow
                sno="C" snoCls="jd-sno-fixed" label="Left Site at" fixed
                start={leftSite.start} finish={leftSite.finish}
                self={leftSite.self_remark} tl={leftSite.team_leader_remark}
                sup={leftSite.supervisor_remark} mgr={leftSite.manager_remark}
                onChange={(field, value) => {
                  if (field === "datetime") {
                    patchActivity({ left_site: { ...leftSite, start: value, finish: value } });
                  } else {
                    patchActivity({ left_site: { ...leftSite, [field]: value } });
                  }
                }}
                job={job}
                canEditStaffActivity={canEditStaffActivity}
              />
            </tbody>
          </table>
        </div>

        {/* ── Save Action Footer ── */}
        <div className="jd-save-bar">
          <span className="jd-save-hint">All changes are saved to article activity</span>
          <button
            className="jd-btn jd-btn-primary"
            disabled={savingActivity[article.id]}
            onClick={() => saveActivity(article.id)}
          >
            {savingActivity[article.id] ? "Saving..." : "Save Activity Details"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Materials Issued tab ─────────────────────────── */
function MaterialsIssuedTab({ materials, onChange, onAdd, onRemove, onSave, saving, job }) {
  // Used Qty / Material Issuance: Team Leader & Admin only (Item 6)
  const canEditUsed = true;
  const canEditReturnOverride = true;
  const canEditRemarks = true;

  return (
    <div className="jd-card">
      <div className="jd-card-header jd-card-header-row">
        <div>
          <h3 className="jd-card-title">Materials Issued</h3>
          <p className="jd-card-subtitle">
            {canEditUsed ? "Used Qty editable by Team Leader" : "Used Qty locked"} ·{" "}
            {canEditReturnOverride ? "Return Qty Override editable by Supervisor" : "Return Override locked (Supervisor only)"}
          </p>
        </div>
      </div>
      <div className="jd-card-body">
        <div className="jd-table-scroll">
          <table className="jd-table">
            <thead>
              <tr>
                <th>S.No</th><th>Material</th><th>Issued Qty</th><th style={{ minWidth: 70 }}>Unit</th>
                <th style={{ minWidth: 110 }}>Used</th><th style={{ minWidth: 100 }}>Balance</th>
                <th style={{ minWidth: 110 }}>Return Qty</th><th>Remarks</th><th></th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m, i) => {
                const issued = parseFloat(m.issued_qty) || 0;
                const used = m.used_qty === "" || m.used_qty == null ? null : parseFloat(m.used_qty) || 0;
                const balance = used === null ? null : issued - used;
                const balanceCls = balance === null ? "" : balance < 0 ? "jd-balance-neg" : balance === 0 ? "jd-balance-zero" : "jd-balance-pos";
                const autoReturn = balance === null ? "" : Math.max(balance, 0);
                const returnVal = m.manual_override ? m.return_qty : autoReturn;
                return (
                  <tr key={m.id ?? i}>
                    <td className="jd-sno-plain">{i + 1}</td>
                    <td className="jd-material-cell">
                      {m.is_extra ? (
                        <div className="jd-extra-material-field">
                          <input type="text" className="jd-input" style={{ width: 100 }} placeholder="Code"
                            disabled={!canEditUsed}
                            value={m.material_code || ""} onChange={(e) => onChange(i, { material_code: e.target.value })} />
                          <span className="jd-extra-tag">Extra</span>
                        </div>
                      ) : m.material_code}
                    </td>
                    <td className="jd-issued-cell">
                      {m.is_extra ? (
                        <input type="number" className="jd-input" style={{ width: 80 }} min="0" placeholder="Qty"
                          disabled={!canEditUsed}
                          value={m.issued_qty ?? ""} onChange={(e) => onChange(i, { issued_qty: e.target.value })} />
                      ) : m.issued_qty}
                    </td>
                    <td>
                      {m.is_extra ? (
                        <input type="text" className="jd-input" style={{ width: 60 }} placeholder="Unit"
                          disabled={!canEditUsed}
                          value={m.unit || ""} onChange={(e) => onChange(i, { unit: e.target.value })} />
                      ) : <span className="jd-unit-badge">{m.unit}</span>}
                    </td>
                    <td>
                      <input type="number" className="jd-input" style={{ width: 110 }} min="0"
                        disabled={!canEditUsed}
                        placeholder={canEditUsed ? "Enter used qty" : "TL only"}
                        value={m.used_qty ?? ""} onChange={(e) => onChange(i, { used_qty: e.target.value })} />
                    </td>
                    <td><span className={`jd-balance ${balanceCls}`}>{balance === null ? "—" : balance}</span></td>
                    <td>
                      <div className="jd-return-wrap">
                        <input type="number" className="jd-input" style={{ width: 110, paddingRight: 22 }} min="0"
                          disabled={!canEditReturnOverride}
                          placeholder={canEditReturnOverride ? "Auto from balance" : "Supervisor only"}
                          value={returnVal === "" ? "" : returnVal}
                          onChange={(e) => onChange(i, { return_qty: e.target.value, manual_override: true })}
                          title={canEditReturnOverride ? "Auto-filled from Balance. Type to override." : "Supervisor only"} />
                        {m.manual_override && <span className="jd-override-icon">✎</span>}
                      </div>
                      <p className="jd-hint">Auto from balance · editable</p>
                    </td>
                    <td>
                      <div className="jd-remarks-field">
                        <input type="text" className="jd-input"
                          disabled={!canEditRemarks}
                          placeholder={canEditRemarks ? (m.is_extra ? "Reason for extra material..." : "Remarks...") : "Supervisor / Manager only"}
                          value={m.remarks || ""} onChange={(e) => onChange(i, { remarks: e.target.value })} />
                        {m.is_extra && canEditUsed && (
                          <button className="jd-remove-row-btn" title="Remove" onClick={() => onRemove(i)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="jd-save-row">
          {canEditUsed && (
            <button className="jd-add-material-btn" onClick={onAdd}>+ Add Extra Material</button>
          )}
          <button className="jd-btn jd-btn-primary" disabled={saving} onClick={onSave} style={{ marginLeft: 8 }}>
            {saving ? "Saving..." : "Save Materials"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Photos tab ─────────────────────────── */
function PhotosTab({ articles, onUpdateArticle, onOpenLightbox, savePhotos, savingPhotos, job }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const role = (user?.role || "").toLowerCase().replace("_", " ");

  const isAdmin = role === "admin";
  const isSupervisor = role === "supervisor";
  const isManager = role === "manager";
  const isTeamLeader = ["team leader", "team_leader", "tl", "teamleader", "leader"].includes(role) || checkIsTeamLeader(user, job);

  const canEditTL = isAdmin || isTeamLeader || isSupervisor || isManager;
  const canEditSupervisor = isAdmin || isSupervisor || isManager;

  const total = articles.reduce((acc, a) => {
    const p = a.photos || {};
    return acc + (p.job_card_photo_url ? 1 : 0) + (p.before?.length || 0) + (p.after?.length || 0);
  }, 0);

  const addPhotos = (article, kind, files) => {
    const p = article.photos || {};
    const existing = p[kind] || [];
    let remaining = files.length;
    const additions = [];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        additions.push({ id: null, url: e.target.result, filename: file.name, file });
        remaining -= 1;
        if (remaining === 0) {
          onUpdateArticle(article.id, { photos: { ...p, [kind]: [...existing, ...additions] } });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (article, kind, idx) => {
    const p = article.photos || {};
    const removed = (p[kind] || [])[idx];
    const next = (p[kind] || []).filter((_, i) => i !== idx);
    const deletedIds = removed?.id
      ? [...(p.deleted_photo_ids || []), removed.id]
      : (p.deleted_photo_ids || []);
    onUpdateArticle(article.id, { photos: { ...p, [kind]: next, deleted_photo_ids: deletedIds } });
  };

  return (
    <div className="jd-card">
      <div className="jd-card-header jd-card-header-row">
        <div>
          <h3 className="jd-card-title">Photos</h3>
          <p className="jd-card-subtitle">Per article · click thumbnail to zoom</p>
        </div>
        <span className="jd-photo-counter">{total} / 10</span>
      </div>
      <div className="jd-table-scroll">
        <table className="jd-table">
          <thead>
            <tr>
              <th>S.No</th><th>Article</th>
              <th style={{ textAlign: "center" }}>Job Card Photo</th>
              <th style={{ textAlign: "center" }}>Before Photo</th>
              <th style={{ textAlign: "center" }}>After Photo</th>
              <th>Self Remark</th><th>Team Leader</th><th>Supervisor</th><th>Save</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((article, i) => {
              const p = article.photos || {};
              const act = article.activity || {};
              const siteActs = act.site_activities || [];

              const siteBeforePhotos = siteActs
                .filter((sa) => sa.before_photo_url)
                .map((sa) => ({ id: `site-b-${sa.id}`, url: sa.before_photo_url, filename: `Before - ${sa.activity_type}` }));
              if (act.before_photo?.url) {
                siteBeforePhotos.unshift({ id: `act-b-${act.before_photo.id || '1'}`, url: act.before_photo.url, filename: "Before Photo" });
              }

              const siteAfterPhotos = siteActs
                .filter((sa) => sa.after_photo_url)
                .map((sa) => ({ id: `site-a-${sa.id}`, url: sa.after_photo_url, filename: `After - ${sa.activity_type}` }));
              (act.after_photos || []).forEach((ap, idx) => {
                if (ap.url) siteAfterPhotos.push({ id: `act-a-${ap.id || idx}`, url: ap.url, filename: `After Photo ${idx + 1}` });
              });

              const combinedPhotos = {
                before: [...siteBeforePhotos, ...(p.before || [])],
                after: [...siteAfterPhotos, ...(p.after || [])],
              };

              return (
                <tr key={article.id}>
                  <td>
                    <div className="jd-photo-sno">{i + 1}</div>
                  </td>
                  <td className="jd-photo-article-name">{article.name ? `${article.code} — ${article.name}` : article.code}</td>
                  <td style={{ textAlign: "center" }}>
                    <PhotoUploadSlot
                      image={p.job_card_photo_url}
                      onUpload={(dataUrl, fileName, file) =>
                        onUpdateArticle(article.id, { photos: { ...p, job_card_photo_url: dataUrl, job_card_photo_file: file } })
                      }
                      onRemove={() =>
                        onUpdateArticle(article.id, { photos: { ...p, job_card_photo_url: null, job_card_photo_file: null } })
                      }
                    />
                  </td>
                  {["before", "after"].map((kind) => (
                    <td key={kind}>
                      <div className="jd-photo-gallery">
                        {(combinedPhotos[kind] || []).map((ph, idx) => (
                          <div className="jd-pthumb" key={idx}>
                            <PhotoImg src={ph.url || ph.filename} alt={kind} />
                            <div className="jd-pt-actions">
                              <button title="View" onClick={() => onOpenLightbox(ph.url || ph.filename, ph.filename)}>👁</button>
                              <button title="Download" onClick={(e) => { e.stopPropagation(); downloadImage(ph.url || ph.filename, ph.filename || "photo.jpg"); }}>⬇</button>
                              {!String(ph.id).startsWith("site-") && !String(ph.id).startsWith("act-") && (
                                <button title="Remove" onClick={() => removePhoto(article, kind, idx)}>✕</button>
                              )}
                            </div>
                          </div>
                        ))}
                        <div className="jd-upload-wrap jd-upload-wrap-sm">
                          <input type="file" accept="image/*" multiple onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length) addPhotos(article, kind, files);
                            e.target.value = "";
                          }} />
                          <div className="jd-upload-box" title="Add photos (multiple allowed)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c7d2fe" strokeWidth="1.8">
                              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                              <circle cx="12" cy="13" r="4" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </td>
                  ))}
                  <td><input type="text" className="jd-input" placeholder="Self remark..." value={p.self_remark || ""} onChange={(e) => onUpdateArticle(article.id, { photos: { ...p, self_remark: e.target.value } })} /></td>
                  <td>
                    <input
                      type="text"
                      className="jd-input"
                      disabled={!canEditTL}
                      placeholder={canEditTL ? "TL remark..." : "TL / Admin only"}
                      value={p.team_leader_remark || ""}
                      onChange={(e) => onUpdateArticle(article.id, { photos: { ...p, team_leader_remark: e.target.value } })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="jd-input"
                      disabled={!canEditSupervisor}
                      placeholder={canEditSupervisor ? "Supervisor remark..." : "Supervisor / Admin only"}
                      value={p.supervisor_remark || ""}
                      onChange={(e) => onUpdateArticle(article.id, { photos: { ...p, supervisor_remark: e.target.value } })}
                    />
                  </td>
                  <td>
                    <button
                      className="jd-btn jd-btn-primary"
                      disabled={!!savingPhotos[article.id]}
                      onClick={() => savePhotos(article)}
                    >
                      {savingPhotos[article.id] ? "Saving..." : "Save"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────── Main Drawer ─────────────────────────── */
export default function JobDetailsDrawer({ jobId, open, onClose, isPage = false }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("articles");
  const [openArticleIds, setOpenArticleIds] = useState(() => new Set());
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = useCallback((url, filename = "") => {
    if (url) setLightbox({ url, filename: filename || "Photo Preview" });
  }, []);
  const [savingActivity, setSavingActivity] = useState({});
  const [savingMaterials, setSavingMaterials] = useState(false);
  const [savingPhotos, setSavingPhotos] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const role = (user?.role || "").toLowerCase().replace("_", " ");

  const fetchJob = useCallback((preserveOpenState = false) => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    API.get(`/jobdetails/${jobId}`)
      .then((res) => {
        const d = res.data;
        if (d.success) {
          setJob(normalizeJobDetails(d.data));
          if (!preserveOpenState) {
            setActiveTab("articles");
            setOpenArticleIds(new Set());
          }
        } else {
          setError(d.message || "Unable to load job details.");
        }
      })
      .catch((err) => {
        console.error("Job details API error:", err);
        setError("Unable to load job details. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  useEffect(() => {
    if (open && jobId) fetchJob();
  }, [open, jobId, fetchJob]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { lightbox ? setLightbox(null) : onClose?.(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox, onClose]);

  // Lock background scroll while the drawer is open (mobile-friendly).
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  const activityOptions = job?.site_activity_options?.length ? job.site_activity_options : DEFAULT_SITE_ACTIVITY_OPTIONS;

  const toggleArticle = (id) => {
    setOpenArticleIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const updateArticle = (articleId, patch) => {
    setJob((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        articles: prev.articles.map((a) => (a.id === articleId ? { ...a, ...patch } : a)),
      };
    });
  };

  const updateMaterial = (idx, patch) => {
    setJob((prev) => {
      if (!prev) return prev;
      const next = [...(prev.materials_issued || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, materials_issued: next };
    });
  };
  const addMaterial = () => {
    setJob((prev) => ({
      ...prev,
      materials_issued: [...(prev?.materials_issued || []), { id: `extra-${Date.now()}`, is_extra: true, material_code: "", issued_qty: "", unit: "", used_qty: "", return_qty: "", remarks: "" }],
    }));
  };
  const removeMaterial = (idx) => {
    setJob((prev) => ({ ...prev, materials_issued: (prev.materials_issued || []).filter((_, i) => i !== idx) }));
  };






  const saveActivity = async (articleId) => {
    if (!job) return;
    const article = job.articles.find((a) => a.id === articleId);
    if (!article || !article.activity) return;

    setSavingActivity((prev) => ({ ...prev, [articleId]: true }));
    try {
      const formData = new FormData();
      formData.append("job_id", job.id);
      formData.append("article_id", articleId);

      // Top Article Overview Box Remarks
      formData.append("article_team_leader_remark", article.team_leader_remark || "");
      formData.append("article_supervisor_remark", article.supervisor_remark || "");

      formData.append(
        "reached_site",
        JSON.stringify(article.activity.reached_site || [])
      );
      formData.append(
        "site_activities",
        JSON.stringify(article.activity.site_activities || [])
      );
      formData.append(
        "cleanup",
        JSON.stringify(article.activity.cleanup || {})
      );
      formData.append(
        "left_site",
        JSON.stringify(article.activity.left_site || {})
      );

      if (article.activity.before_photo?.file) {
        formData.append("before_photo", article.activity.before_photo.file);
      }

      // Row 1 Before Photo meta — send ONLY Row 1's own remarks!
      formData.append(
        "before_photo_meta",
        JSON.stringify({
          ...(article.activity.before_photo || {}),
          team_leader_remark: article.activity.before_photo?.team_leader_remark || "",
          supervisor_remark: article.activity.before_photo?.supervisor_remark || "",
          manager_remark: article.activity.before_photo?.manager_remark || "",
        })
      );

      if (article.activity.after_photos?.length) {
        article.activity.after_photos.forEach((photo) => {
          if (photo.file) {
            formData.append("after_photos[]", photo.file);
          }
        });
      }
      formData.append(
        "after_photo_meta",
        JSON.stringify({
          ...(article.activity.after_photo_meta || {}),
          team_leader_remark: article.activity.after_photo_meta?.team_leader_remark || "",
          supervisor_remark: article.activity.after_photo_meta?.supervisor_remark || "",
          manager_remark: article.activity.after_photo_meta?.manager_remark || "",
        })
      );

      (article.activity.site_activities || []).forEach((actItem, idx) => {
        if (actItem.before_photo_file) {
          formData.append(`site_activity_before_${idx}`, actItem.before_photo_file);
        }
        if (actItem.after_photo_file) {
          formData.append(`site_activity_after_${idx}`, actItem.after_photo_file);
        }
      });

      const res = await API.post("/job/save-activity", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success) {
        alert("Activity details saved successfully.");
        fetchJob(true);
      } else {
        alert(res.data?.message || "Failed to save activities.");
      }
    } catch (err) {
      console.error("saveActivity error:", err);
      alert(err.response?.data?.message || "Failed to save activities.");
    } finally {
      setSavingActivity((prev) => ({ ...prev, [articleId]: false }));
    }
  };

  const saveMaterials = async () => {
    if (!job) return;
    setSavingMaterials(true);
    try {
      const payload = {
        job_id: job.id,
        materials: (job.materials_issued || []).map((m) => ({
          id: m.id && typeof m.id === "number" ? m.id : null,
          material_name: m.material_name,
          issued_qty: Number(m.issued_qty) || 0,
          unit: m.unit || "pcs",
          used: m.used_qty !== undefined && m.used_qty !== null ? Number(m.used_qty) : (m.used !== undefined && m.used !== null ? Number(m.used) : null),
          return_qty: m.return_qty !== undefined && m.return_qty !== null ? Number(m.return_qty) : null,
          remarks: m.remarks || "",
        })),
      };
      const res = await API.post("/job/update-material", payload);
      if (res.data?.success) {
        alert("Materials issued saved successfully.");
        fetchJob();
      } else {
        alert(res.data?.message || "Failed to save materials.");
      }
    } catch (err) {
      console.error("saveMaterials error:", err);
      alert(err.response?.data?.message || "Failed to save materials.");
    } finally {
      setSavingMaterials(false);
    }
  };

  const savePhotos = async (article) => {
    if (!job) return;
    setSavingPhotos((prev) => ({ ...prev, [article.id]: true }));
    try {
      const p = article.photos || {};
      const formData = new FormData();
      formData.append("job_id", job.id);
      formData.append("article_id", article.id);

      if (p.job_card_photo_file) {
        formData.append("job_card_photo", p.job_card_photo_file);
      }
      if (p.before && p.before.length > 0) {
        p.before.forEach((item) => {
          if (item.file) formData.append("before_photos[]", item.file);
        });
      }
      if (p.after && p.after.length > 0) {
        p.after.forEach((item) => {
          if (item.file) formData.append("after_photos[]", item.file);
        });
      }
      if (p.deleted_photo_ids && p.deleted_photo_ids.length > 0) {
        p.deleted_photo_ids.forEach((id) => {
          formData.append("deleted_photo_ids[]", id);
        });
      }

      const response = await API.post("/job/update-photos", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data?.success) {
        alert("Photos saved successfully");
        fetchJob(true);
      } else {
        alert(response.data?.message || "Failed to save photos.");
      }
    } catch (err) {
      console.error("savePhotos error:", err);
      alert(err.response?.data?.message || "Unable to save photos.");
    } finally {
      setSavingPhotos((prev) => ({ ...prev, [article.id]: false }));
    }
  };

  if (!open) return null;

  const content = (
    <div className={isPage ? "jd-page-container" : `jd-drawer ${open ? "jd-open" : ""}`} role="dialog" aria-modal={!isPage}>
      <div className="jd-drawer-topbar">
        <div className="jd-drawer-crumb">
          <span style={{ cursor: "pointer" }} onClick={onClose}>Dashboard</span>
          {job && <><span>›</span><span className="jd-crumb-current">Job #{job.id} — {job.client_name}</span></>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!isEditing && (role === "admin" || role === "supervisor") && (
            <button className="jd-btn jd-btn-primary" style={{ padding: "4px 12px", fontSize: ".8rem" }} onClick={() => setIsEditing(true)}>
              ✏️ Edit Job Card
            </button>
          )}
          <button className="jd-drawer-close" onClick={onClose}>Close ✕</button>
        </div>
      </div>

      <div className="jd-drawer-body">
        {isEditing && job ? (
          <div style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: "#1e293b" }}>Editing Job #{job.id}</h3>
              <button className="jd-btn jd-btn-ghost" onClick={() => setIsEditing(false)}>← Cancel Edit</button>
            </div>
            <CreateJobFormNew editJobId={job.id} onSaveSuccess={() => { setIsEditing(false); fetchJob(); }} onCloseEdit={() => setIsEditing(false)} />
          </div>
        ) : (
          <>
            {loading && (
              <div className="jd-state-wrap">
                <div className="jd-spinner-lg" />
                <span>Loading job details…</span>
              </div>
            )}

            {!loading && error && (
              <div className="jd-state-wrap">
                <span>{error}</span>
                <button className="jd-retry-btn" onClick={fetchJob}>Retry</button>
              </div>
            )}

            {!loading && !error && job && (
              <>
            <div className="jd-card">
              <div className="jd-summary-row">
                <div className="jd-summary-fields">
                  <div>
                    <div className="jd-summary-label">Job ID</div>
                    <div className="jd-summary-value-id">#{job.id}</div>
                  </div>
                  <div className="jd-summary-divider" />
                  <div>
                    <div className="jd-summary-label">Client</div>
                    <div className="jd-summary-value-strong">{job.client_name}</div>
                  </div>
                  <div className="jd-summary-divider" />
                  <div>
                    <div className="jd-summary-label">Location</div>
                    <div className="jd-summary-value">{job.location}</div>
                  </div>
                  <div className="jd-summary-divider" />
                  <div>
                    <div className="jd-summary-label">Staff</div>
                    <div className="jd-summary-value">{(job.staff || []).map((s) => s.name).join(", ") || "—"}</div>
                  </div>
                  <div className="jd-summary-divider" />
                  <div>
                    <div className="jd-summary-label">Team Leader</div>
                    <div className="jd-summary-value-strong" style={{ color: "#7c3aed", display: "flex", alignItems: "center", gap: "4px" }}>
                      👑 {job.team_leader?.name || (typeof job.team_leader === "string" ? job.team_leader : "Not Assigned")}
                    </div>
                  </div>
                  <div className="jd-summary-divider" />
                  <div>
                    <div className="jd-summary-label">Status</div>
                    {(() => {
                      const overallStatus = computeOverallJobStatus(job);
                      return <span className={badgeClass(overallStatus)}>{overallStatus}</span>;
                    })()}
                  </div>
                </div>
              </div>
              <div className="jd-tabs">
                {[
                  ["articles", "Articles & Activities"],
                  ["materials", "Materials Issued"],
                  ["photos", "Photos"],
                ].map(([key, label]) => (
                  <button key={key} className={`jd-tab-btn ${activeTab === key ? "jd-active" : ""}`} onClick={() => setActiveTab(key)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className={`jd-tab-panel ${activeTab === "articles" ? "jd-active" : ""}`}>
              <div className="jd-notice">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="jd-notice-icon">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>Click any article to expand and fill activity details. Fixed rows (A–C) apply to all articles. Add extra site activities as needed.</span>
              </div>
              {(job.articles || []).map((article, idx) => (
                  <ArticleAccordion
                      key={article.id}
                      article={article}
                      index={idx}
                      isOpen={openArticleIds.has(article.id)}
                      onToggle={() => toggleArticle(article.id)}
                      activityOptions={activityOptions}
                      onUpdateArticle={updateArticle}
                      onOpenLightbox={openLightbox}
                      saveActivity={saveActivity}
                      savingActivity={savingActivity}
                      job={job}
                  />
              ))}
              {!job.articles?.length && (
                <div className="jd-state-wrap jd-state-wrap-inline">No articles assigned to this job.</div>
              )}
            </div>

            <div className={`jd-tab-panel ${activeTab === "materials" ? "jd-active" : ""}`}>
              <MaterialsIssuedTab
                materials={job.materials_issued || []}
                onChange={updateMaterial}
                onAdd={addMaterial}
                onRemove={removeMaterial}
                onSave={saveMaterials}
                saving={savingMaterials}
                job={job}
              />
            </div>

            <div className={`jd-tab-panel ${activeTab === "photos" ? "jd-active" : ""}`}>
              <PhotosTab
                articles={job.articles || []}
                onUpdateArticle={updateArticle}
                onOpenLightbox={openLightbox}
                savePhotos={savePhotos}
                savingPhotos={savingPhotos}
                job={job}
              />
            </div>
          </>
        )}
          </>
        )}
      </div>

      <Lightbox src={lightbox?.url} filename={lightbox?.filename} onClose={() => setLightbox(null)} />
    </div>
  );

  if (isPage) {
    return content;
  }

  return createPortal(
    <>
      <div className={`jd-overlay ${open ? "jd-open" : ""}`} onClick={onClose} />
      {content}
    </>,
    document.body
  );
}